import clsx from "clsx";
import React, { useCallback } from "react";
import { shallowEqual, useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import AppSearch from "components/AppSearch/AppSearch";
import {
  WithClickBlockerProps,
  withClickBlocker
} from "components/BlockableClickContext";
import ErrorBoundary from "components/ErrorBoundary";
import FacilitySelect from "components/FacilitySelect";
import AssignedToMeToggle from "components/VetExamsAssignment/AssignedToMeToggle";
import { IExamsTableRowData } from "components/VetExamsTable/types";
import { useAppSelector } from "hooks/redux/useAppSelector";
import {
  setSearchValue,
  setSelectedPreRaceFacility,
  setShowAssignedToMeOnly
} from "store/actions/preRaceScreeningPage";
import { checkCurrentRaceEntriesTableTab } from "../helper";
import PickerFilter from "./PickerFilter";

import useStyles from "./styles";

interface IProps {
  upcomingRaceEntriesQuery: racehorse360.IUpcomingRaceEntryFilter;
  tableRows: IExamsTableRowData[];
}

const PreRaceScreeningPageHeader: React.FC<
  IProps & WithClickBlockerProps
> = props => {
  const { upcomingRaceEntriesQuery, handleBlockableClick, tableRows } = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const { searchValue, selectedFacility, selectedTab, showAssignedToMeOnly } =
    useAppSelector(
      state => ({
        searchValue: state.preRaceScreeningPage.searchValue,
        selectedFacility: state.preRaceScreeningPage.selectedFacility,
        selectedTab: state.preRaceScreeningPage.selectedTab,
        showAssignedToMeOnly: state.preRaceScreeningPage.showAssignedToMeOnly
      }),
      shallowEqual
    );

  const { isEntryExamsTab, isRaceDayTab } = checkCurrentRaceEntriesTableTab(
    selectedTab.value
  );

  const handleFacilityChange = useCallback(
    (facility: racehorse360.IFacility) => {
      dispatch(setSelectedPreRaceFacility(facility));
    },
    [dispatch]
  );

  const handleAssignedToMeChange = useCallback(() => {
    dispatch(setShowAssignedToMeOnly(!showAssignedToMeOnly));
  }, [dispatch, showAssignedToMeOnly]);

  const handleSearch = (value: string) => {
    handleBlockableClick(() => dispatch(setSearchValue(value)));
  };

  return (
    <div
      className={clsx(classes.pageHeader, {
        raceDayTab: !isEntryExamsTab
      })}
    >
      <div className={classes.firstLevelFilters}>
        <div className={classes.facility}>
          <ErrorBoundary>
            <FacilitySelect
              selectedFacility={selectedFacility}
              onChange={handleFacilityChange}
            />
          </ErrorBoundary>
        </div>
        {(isEntryExamsTab || isRaceDayTab) && (
          <div className={classes.assignedToMe}>
            <AssignedToMeToggle
              isChecked={showAssignedToMeOnly}
              onChange={handleAssignedToMeChange}
            />
          </div>
        )}
      </div>

      <div className={classes.secondLevelFilters}>
        <div className={classes.searchFilter}>
          <ErrorBoundary>
            <AppSearch
              className={classes.search}
              isForceSearch
              placeholder={"Search Horses or Trainers"}
              upcomingRaceEntriesFilter={upcomingRaceEntriesQuery}
              value={searchValue}
              onSearch={handleSearch}
              clearSearch={Boolean(!searchValue)}
            />
          </ErrorBoundary>
        </div>

        <PickerFilter
          className={classes.pickerFilterDesktop}
          tableRows={tableRows}
        />
      </div>

      <div className={classes.thirdLevelFilters}>
        <PickerFilter tableRows={tableRows} />
      </div>
    </div>
  );
};

export default withClickBlocker<IProps>(React.memo(PreRaceScreeningPageHeader));
